import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table as BootstrapTable,
  Card,
  CardBody,
  CardTitle,
  Spinner,
  Button,
  Tooltip,
  UncontrolledDropdown,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';

import ActionIcon from './ActionIcon';
import Avatar from './Avatar';
import Number from './Number';
import { truncate, determineStatus, getPrice } from '../utils';
import StatusText from './StatusText';
import UncontrolledTooltip from 'reactstrap/lib/UncontrolledTooltip';

function EmptyTable({ primary, secondary }) {
  return (
    <Card className="w-50 my-5 mx-auto bg-light text-dark">
      <CardBody className="text-center">
        <CardTitle tag="h4" className="font-weight-bold">
          {primary}
        </CardTitle>
        <p className="text-muted mb-0">{secondary}</p>
      </CardBody>
    </Card>
  );
}

function Table({
  className = '',
  itemId = '_id',
  columns,
  data,
  actions,
  paginationData,
  onNextPage,
  onPrevPage,
  pagination = true,
  isLoading,
  hasError,
  EmptyTableComponent = EmptyTable,
  EmptyTableProps = {},
}) {
  const { t } = useTranslation('common');
  const status = determineStatus({ isLoading, hasError, data });

  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState('');

  const toggleActionMenu = (id) => () => {
    setIsActionMenuOpen((open) => {
      console.log({ open });
      setCurrentItem(!open ? id : '');
      return !open;
    });
  };

  const truncateText = (text) => {
    return (
      <>
        <div className="d-lg-block d-sm-none text-black-50">
          {truncate(text)}
        </div>
        <div className="d-lg-none d-sm-block">{text}</div>
      </>
    );
  };

  return (
    <>
      {' '}
      <BootstrapTable className={`shadow-sm ${className}`}>
        <thead>
          <tr className="bg-light text-dark">
            {columns.map((column) => (
              <th
                key={column.header}
                className={clsx(column?.className, 'text-nowrap')}
              >
                {column.header}
              </th>
            ))}
            {actions !== 'forCopyright' && (
              <th className="text-center text-nowrap">{t('actions.header')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {status === 'loaded' &&
            data?.map((obj, i) => (
              <tr key={obj[itemId]} className="bg-light pointer align-middle">
                {columns.map((column, columnIndex) => (
                  <td
                    key={column.key + columnIndex}
                    data-label={column.key}
                    className={`text-dark ${column.className}`}
                    onClick={column?.onClick?.(obj)}
                  >
                    {column.render ? (
                      column.render(obj, column)
                    ) : (
                      <>
                        {column.key === 'index' &&
                          `${
                            i +
                            1 +
                            paginationData?.perPage * (paginationData?.page - 1)
                          }`}
                        {column.truncate && truncateText(obj[column.key])}
                        {!column.truncate &&
                          column.key !== 'index' &&
                          obj[column.key]}
                      </>
                    )}
                  </td>
                ))}
                {actions !== 'forCopyright' && (
                  <td
                    className=" d-flex justify-content-center"
                    data-label="actions"
                    style={{ zIndex: 1000 }}
                  >
                    <UncontrolledDropdown>
                      <DropdownToggle tag="span">
                        <FontAwesomeIcon icon="bars" />
                      </DropdownToggle>
                      <DropdownMenu style={{ zIndex: 1000 }}>
                        {actions?.map((action, i) => {
                          if (!(action?.shouldShow?.(obj, action) ?? true)) {
                            return null;
                          }

                          if (action?.shouldHide?.(obj, action)) {
                            return null;
                          }
                          const shouldShowPremiumIcon =
                            action?.shouldShowPremiumIcon?.(obj, action);
                          const isDisabled =
                            shouldShowPremiumIcon ||
                            action?.isDisabled ||
                            action?.disableOn?.(obj, action) ||
                            false;

                          return (
                            <DropdownItem
                              key={action.key + i}
                              disabled={isDisabled}
                              onClick={() => action?.onClick?.(obj)}
                            >
                              <ActionItem
                                className={`mr-2 ${action.className}`}
                                icon={action.icon}
                                index={i}
                                tag={action.isLink ? 'a' : 'span'}
                                onClick={() => action?.onClick?.(obj)}
                                href={obj[action.key]}
                                shouldShowPremiumIcon={shouldShowPremiumIcon}
                                disabled={isDisabled}
                                target="_blank"
                                label={action.label}
                              />
                            </DropdownItem>
                          );
                        })}
                      </DropdownMenu>
                    </UncontrolledDropdown>

                    {/* <Dropdown size="sm" isOpen={currentItem === obj[itemId]} toggle={toggleActionMenu(obj[itemId])}>
                    <DropdownToggle className="border-2 border-black" size="sm" color="light">
                      <FontAwesomeIcon icon="bars" />
                    </DropdownToggle>
                    <DropdownMenu right>
                      <DropdownItem>Action</DropdownItem>
                      <DropdownItem>Another Action</DropdownItem>
                    </DropdownMenu>
                  </Dropdown> */}
                  </td>
                )}
              </tr>
            ))}
        </tbody>
      </BootstrapTable>
      {status === 'error' && <div className="text-light">Error</div>}
      {status === 'loading' && (
        <div className="loader-container text-dark d-flex justify-content-center align-items-center flex-column mt-n2">
          <Spinner />
        </div>
      )}
      {status === 'empty' && <EmptyTableComponent {...EmptyTableProps} />}
      {status === 'loaded' && pagination && (
        <CustomPagination
          {...paginationData}
          count={data?.length}
          onNext={onNextPage}
          onPrev={onPrevPage}
          className="float-right mb-3 w-100"
        />
      )}
    </>
  );
}

export function CustomPagination({
  className,
  numPages = 1,
  page = 1,
  perPage,
  count,
  total,
  onNext,
  onPrev,
}) {
  const { t } = useTranslation('common');
  return (
    <div
      className={`d-flex justify-content-between mt-n2 mb-4 pagination-mobile ${className}`}
    >
      <small
        className="float-left d-inline-block align-self-center justify-content-start"
        dangerouslySetInnerHTML={{
          __html: t('showingDocuments', { amount: count ?? perPage, total }),
        }}
      />
      <div className="d-flex">
        <Button
          color="light"
          className="mr-2 rounded"
          size="sm"
          disabled={parseInt(page) === 1}
          onClick={onPrev}
        >
          <FontAwesomeIcon icon="chevron-left" className="text-dark mr-2" />{' '}
          {t('actions.previous')}
        </Button>
        <Number
          className="bg-light text-dark mr-2 px-3 text-center d-flex shadow-sm"
          color="dark"
          bg={'light'}
        >
          <div className="align-self-center">{page}</div>
        </Number>
        <Button
          color="light"
          className="rounded"
          size="sm"
          onClick={onNext}
          disabled={parseInt(page) === numPages}
        >
          {' '}
          {t('actions.next')}
          <FontAwesomeIcon icon="chevron-right" className="text-dark ml-2" />
        </Button>
      </div>
    </div>
  );
}

Table.propTypes = {
  className: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      truncate: PropTypes.bool,
      className: PropTypes.string,
    })
  ),
  data: PropTypes.array,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      className: PropTypes.string,
      hideOnStatus: PropTypes.string,
      icon: PropTypes.string.isRequired,
      isLink: PropTypes.bool,
      onClick: PropTypes.func,
    })
  ),
};

export default Table;

function ActionItem({
  className,
  icon,
  onClick = () => {},
  tag: Tag = 'span',
  disabled,
  shouldShowPremiumIcon,
  label,
  index,
  ...props
}) {
  const id = `premium-action-${label}-${icon}-${index}`;
  const handleClick = () => {
    if (disabled) return;
    onClick();
  };

  return (
    <>
      {shouldShowPremiumIcon && (
        <UncontrolledTooltip target={id} placement="top">
          Esta funcion esta disponible a partir del plan basico
        </UncontrolledTooltip>
      )}
      <Tag
        {...props}
        className={clsx(
          `text-sm rounded`,
          Tag === 'a' && 'text-dark',
          className
        )}
        onClick={handleClick}
        id={id}
      >
        {/*action-bg text-light*/}
        {shouldShowPremiumIcon && (
          <FontAwesomeIcon
            className={`mr-2 text-warning`}
            size="lg"
            icon="crown"
            fixedWidth
          />
        )}
        <FontAwesomeIcon
          className={clsx(disabled ? `text-muted` : 'text-dark')}
          size="lg"
          icon={icon}
          fixedWidth
        />{' '}
        {label}
      </Tag>
    </>
  );
}

ActionIcon.propTypes = {
  onClick: PropTypes.func,
  icon: PropTypes.string,
  className: PropTypes.string,
};
